import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { OwnerCtx } from '../unsaved/unsaved';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';

/** Props every dialog opened with `open()` receives. */
export interface DialogProps<R = void> {
  /** Close the dialog, optionally with a result for whoever opened it. */
  close(result?: R): void;
}

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red button: for things that are hard to undo. */
  danger?: boolean;
}

interface DialogApi {
  /** Opens any dialog component and resolves with what it closes with. */
  open<P extends object, R = void>(component: ComponentType<P & DialogProps<R>>, props: P): Promise<R | undefined>;
  /** "Are you sure?" Resolves true when confirmed. */
  confirm(opts: ConfirmOptions): Promise<boolean>;
}

const Ctx = createContext<DialogApi | null>(null);

interface Entry {
  id: number;
  render(): ReactNode;
}

/**
 * One place that opens dialogs, so any page can do
 *   const dialog = useDialog();
 *   const saved = await dialog.open(UserDialog, { user });
 *   if (await dialog.confirm({ title: 'Deactivate Tony?', confirmLabel: 'Deactivate', danger: true })) …
 * without keeping open/closed state for every dialog.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Entry[]>([]);
  const next = useRef(1);

  const open = useCallback(<P extends object, R>(Component: ComponentType<P & DialogProps<R>>, props: P) => {
    return new Promise<R | undefined>((resolve) => {
      const id = next.current++;
      const close = (result?: R) => {
        setStack((s) => s.filter((e) => e.id !== id));
        resolve(result);
      };
      // Each dialog's forms report unsaved changes under the dialog's own name.
      setStack((s) => [...s, { id, render: () => <OwnerCtx.Provider key={id} value={`dialog-${id}`}><Component {...props} close={close} /></OwnerCtx.Provider> }]);
    });
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => open(ConfirmDialog, opts).then((r) => r === true), [open]);
  const api = useMemo(() => ({ open, confirm }), [open, confirm]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {stack.map((e) => e.render())}
    </Ctx.Provider>
  );
}

export function useDialog(): DialogApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useDialog needs a <DialogProvider>');
  return api;
}

function ConfirmDialog({ title, message, confirmLabel, cancelLabel = 'Cancel', danger, close }: ConfirmOptions & DialogProps<boolean>) {
  return (
    <Dialog open onClose={() => close(false)} maxWidth="xs" fullWidth aria-labelledby="confirm-title">
      <DialogTitle id="confirm-title">{title}</DialogTitle>
      {message && (
        <DialogContent>
          <DialogContentText component="div" sx={{ color: 'text.secondary' }}>
            {message}
          </DialogContentText>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => close(false)}>{cancelLabel}</Button>
        <Button variant="contained" color={danger ? 'error' : 'primary'} onClick={() => close(true)} autoFocus>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
